/**
 * Règles de complétude d'une adresse postale, partagées par TOUS les points
 * d'entrée qui en collectent une (inscription école classic/partner,
 * finalisation d'inclusion, fast register Habitation/RC/Santé-Mobilité).
 *
 * Bug historique (STU-28) : le code postal était exigé pour tous les pays.
 * Beaucoup de pays n'en ont pas — et Google Places ne renvoie alors aucun
 * composant `postal_code` — ce qui bloquait purement et simplement les clients
 * étrangers. À l'inverse le `country` n'était pas validé côté front alors que
 * le back le refuse vide (`NotBlank` dans `AddressType`), d'où des 400 opaques.
 *
 * Ces règles doivent rester alignées sur `src/Form/AddressType.php` côté back.
 */

export interface AddressLike {
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
}

export type AddressField = 'street' | 'postalCode' | 'city' | 'country';

/** Valeurs possibles du champ pays pour la France : code ISO court renvoyé par
 *  Google Places (`FR`), code à 3 lettres, ou saisie manuelle en toutes lettres. */
const FRANCE_ALIASES = ['fr', 'fra', 'france'];

/**
 * Un champ d'adresse est-il vide ? Une valeur composée uniquement d'espaces
 * compte comme vide — c'est LA règle du projet, exportée pour que l'UI la
 * partage (affichage du récapitulatif) au lieu d'en écrire une variante.
 */
export const isBlank = (value?: string | null): boolean =>
  !value || value.trim().length === 0;

/**
 * L'adresse porte-t-elle au moins une valeur ? Question DISTINCTE de la
 * complétude : elle dit si l'utilisateur a réellement saisi quelque chose.
 *
 * Les écrans s'en servent pour décider qu'un champ est « touché » : une adresse
 * intégralement vide ne l'est pas — c'est exactement ce que remonte le lien
 * « Revenir à la recherche » du composant `AddressInput`, et signaler une
 * erreur sur une page que l'utilisateur vient d'effacer lui-même n'aurait
 * aucun sens (STU-28).
 *
 * Bâtie sur `isBlank` comme le reste du module : un champ fait uniquement
 * d'espaces ne compte pas.
 */
export const hasAnyAddressValue = (address?: AddressLike | null): boolean =>
  [address?.street, address?.postalCode, address?.city, address?.country].some(
    (field) => !isBlank(field),
  );

/**
 * Le pays désigne-t-il la France ? Sert à décider si le code postal est exigé.
 * Tolérant sur la casse et les espaces, car la valeur vient soit de Google
 * (`FR`), soit d'une saisie libre de l'utilisateur en mode manuel.
 */
export const isFrance = (country?: string | null): boolean =>
  !isBlank(country) && FRANCE_ALIASES.includes((country as string).trim().toLowerCase());

/**
 * Le code postal n'est obligatoire qu'en France : ailleurs, il peut ne pas
 * exister. Exporté pour que l'UI puisse adapter le libellé du champ.
 */
export const isPostalCodeRequired = (country?: string | null): boolean =>
  isFrance(country);

/**
 * Liste les champs manquants d'une adresse, dans l'ordre d'affichage du
 * formulaire. Un tableau vide signifie que l'adresse est acceptable — côté
 * front ET côté back.
 */
export const getMissingAddressFields = (
  address?: AddressLike | null,
): AddressField[] => {
  const missing: AddressField[] = [];

  if (isBlank(address?.street)) missing.push('street');
  if (isPostalCodeRequired(address?.country) && isBlank(address?.postalCode)) {
    missing.push('postalCode');
  }
  if (isBlank(address?.city)) missing.push('city');
  if (isBlank(address?.country)) missing.push('country');

  return missing;
};

/** Raccourci : l'adresse est-elle complète ? */
export const isAddressComplete = (address?: AddressLike | null): boolean =>
  getMissingAddressFields(address).length === 0;
